import { useCallback, useEffect, useState } from 'react'
import { programsApi, type WorkBucket } from '../lib/api.js'

export type WorkItemReason = 'no-media' | 'missing-file' | 'no-branding' | 'missing-epg'

export interface CatalogOverview {
  loading:           boolean
  programCount:      number
  totalDurationSec:  number
  collectionCount:   number
  seriesCount:       number
  mediaCount:        number
  mediaNeedingCheck: number
  /** Programmes à traiter : un compteur exact et les premiers titres. */
  work:              Record<WorkItemReason, WorkBucket>
  workTotal:         number
  reload:            () => void
}

const EMPTY_BUCKET: WorkBucket = { count: 0, sample: [] }

const EMPTY_WORK: Record<WorkItemReason, WorkBucket> = {
  'no-media': EMPTY_BUCKET,
  'missing-file': EMPTY_BUCKET,
  'no-branding': EMPTY_BUCKET,
  'missing-epg': EMPTY_BUCKET,
}

/**
 * Agrégats du catalogue pour l'écran Programmes sans sélection.
 *
 * Tout est compté en base par /programs/catalog-overview. L'écran parcourait
 * auparavant le catalogue entier — cinq pages de cent programmes, relations
 * comprises — pour afficher quatre compteurs et trois listes de quatre titres.
 */
export function useCatalogOverview(channelId: string | null, refreshKey = 0): CatalogOverview {
  const [state, setState] = useState<Omit<CatalogOverview, 'reload'>>({
    loading: false,
    programCount: 0,
    totalDurationSec: 0,
    collectionCount: 0,
    seriesCount: 0,
    mediaCount: 0,
    mediaNeedingCheck: 0,
    work: EMPTY_WORK,
    workTotal: 0,
  })
  const [manualKey, setManualKey] = useState(0)
  const reload = useCallback(() => setManualKey(key => key + 1), [])

  useEffect(() => {
    if (!channelId) return
    let cancelled = false
    setState(current => ({ ...current, loading: true }))

    const run = async () => {
      // Le serveur pousse un PROGRAMS_UPDATED par programme touché : un import
      // de série en déclenche autant que d'épisodes. Ce délai laisse la rafale
      // se refermer sur un seul appel.
      await new Promise(resolve => setTimeout(resolve, 400))
      if (cancelled) return

      const res = await programsApi.catalogOverview({ channelId, catalog: 'programs' })
      if (cancelled) return

      setState({ loading: false, ...res })
    }

    run().catch(() => {
      if (!cancelled) setState(current => ({ ...current, loading: false }))
    })

    return () => { cancelled = true }
  }, [channelId, refreshKey, manualKey])

  return { ...state, reload }
}
